import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Download,
  HeartPulse,
  Loader2,
  PanelRightOpen,
  RefreshCw,
  X,
} from 'lucide-react';
import { useDailyCondition, useDailyConditionStats, useDailyConditionTable } from '../hooks';
import { dailyConditionsApi } from '../api';
import type { DailyConditionStatus, DailyConditionTableRow, RiskFlag } from '../api/dailyConditions';
import { formatDateTime } from '../utils/date';
import { GROUP_DOCS, columnDoc } from '../utils/dailyConditionGlossary';
import {
  PROFILE_GROUPS,
  RESULT_COLUMNS,
  buildProfileColumns,
  downloadCsv,
  profileValue,
  summarize,
  EXCLUDABLE_NAME_TERMS,
  isExcludedAccount,
  type ExcludableTerm,
  toCsv,
  utcDaysBetween,
  type ProfileColumn,
} from '../utils/dailyConditionTable';

// Day picker steps through days that actually have rows
const STATS_DAYS = 90;
// Days fetched in parallel for the all-time export
const EXPORT_CONCURRENCY = 4;
const FLAGS: RiskFlag[] = ['green', 'yellow', 'red'];

const flagStyle: Record<RiskFlag, string> = {
  green: 'bg-green-50 text-green-700 border-green-200',
  yellow: 'bg-amber-50 text-amber-700 border-amber-200',
  red: 'bg-red-50 text-red-700 border-red-200',
};

const statusStyle: Record<DailyConditionStatus, string> = {
  completed: 'bg-slate-100 text-slate-600',
  failed: 'bg-red-100 text-red-700',
  processing: 'bg-blue-100 text-blue-700',
  pending: 'bg-slate-100 text-slate-500',
};

const formatDuration = (ms: number | null): string => (ms == null ? '-' : `${(ms / 1000).toFixed(1)}s`);

const FlagBadge: React.FC<{ flag: RiskFlag | null }> = ({ flag }) =>
  flag ? (
    <span className={`px-2 py-0.5 rounded border text-xs font-medium ${flagStyle[flag]}`}>{flag}</span>
  ) : (
    <span className="text-slate-300">-</span>
  );

const StatusBadge: React.FC<{ status: DailyConditionStatus }> = ({ status }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusStyle[status] ?? statusStyle.pending}`}>
    {status}
  </span>
);

const JsonBlock: React.FC<{ title: string; value: unknown; defaultOpen?: boolean; testId?: string }> = ({
  title,
  value,
  defaultOpen = false,
  testId,
}) => {
  const isEmpty =
    value == null ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && Object.keys(value as object).length === 0);
  return (
    <details open={defaultOpen} className="border border-slate-200 rounded-lg" data-testid={testId}>
      <summary className="px-3 py-2 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-50 flex items-center gap-2">
        {title}
        {isEmpty && <span className="text-xs font-mono text-amber-600">{JSON.stringify(value ?? null)}</span>}
      </summary>
      <pre className="bg-slate-50 text-slate-700 p-3 text-xs overflow-auto max-h-96 border-t border-slate-200">
        {JSON.stringify(value ?? null, null, 2)}
      </pre>
    </details>
  );
};

// ── Detail panel ────────────────────────────────────────────

const DetailPanel: React.FC<{ id: string; onClose: () => void }> = ({ id, onClose }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useDailyCondition(id);
  const result = data?.result as
    | {
        reason?: string;
        message?: string;
        daily_guide?: { type?: string; description?: string };
        suspected_injury?: string | null;
      }
    | null
    | undefined;

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-label={t('dailyConditions.detail')}>
      <div className="absolute inset-0 bg-slate-900/20" onClick={onClose} />
      <aside className="relative w-full max-w-2xl h-full bg-white shadow-xl overflow-y-auto" data-testid="dc-detail">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-800">
              {data?.user_name || data?.user_id || t('dailyConditions.detail')}
            </h3>
            {data && <p className="text-xs text-slate-500 font-mono">{data.user_id}</p>}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-slate-100"
            aria-label={t('dailyConditions.close')}
          >
            <X size={18} />
          </button>
        </div>

        {isLoading && (
          <div className="p-10 flex justify-center">
            <Loader2 className="animate-spin text-slate-400" />
          </div>
        )}
        {isError && <p className="p-5 text-sm text-red-600">{t('dailyConditions.loadError')}</p>}

        {data && (
          <div className="p-5 space-y-5">
            <div className="flex items-center gap-2">
              <StatusBadge status={data.status} />
              <FlagBadge flag={data.risk_flag} />
              {data.empty_request && (
                <span className="px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700 border border-amber-200">
                  {t('dailyConditions.emptyRequest')}
                </span>
              )}
            </div>

            {result && (
              <section className="space-y-2" data-testid="dc-detail-result">
                <h4 className="text-sm font-semibold text-slate-700">{t('dailyConditions.result')}</h4>
                <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-2 text-sm">
                  <dt className="text-slate-500">{t('dailyConditions.message')}</dt>
                  <dd className="text-slate-800">{result.message ?? '-'}</dd>
                  <dt className="text-slate-500">{t('dailyConditions.reason')}</dt>
                  <dd className="text-slate-800">{result.reason ?? '-'}</dd>
                  <dt className="text-slate-500">{t('dailyConditions.dailyGuide')}</dt>
                  <dd className="text-slate-800">
                    {result.daily_guide ? (
                      <>
                        <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded mr-2">
                          {result.daily_guide.type}
                        </span>
                        {result.daily_guide.description}
                      </>
                    ) : (
                      '-'
                    )}
                  </dd>
                  <dt className="text-slate-500">{t('dailyConditions.suspectedInjury')}</dt>
                  <dd className="text-slate-800">{result.suspected_injury || '-'}</dd>
                </dl>
              </section>
            )}

            {data.error && (
              <section className="space-y-2">
                <h4 className="text-sm font-semibold text-red-700">{t('dailyConditions.error')}</h4>
                <pre className="bg-red-50 text-red-800 p-3 rounded text-xs overflow-auto whitespace-pre-wrap">
                  {JSON.stringify(data.error, null, 2)}
                </pre>
              </section>
            )}

            <section className="space-y-2">
              <JsonBlock
                title={t('dailyConditions.inputRequest')}
                value={data.context.request}
                defaultOpen
                testId="dc-detail-request"
              />
              <JsonBlock
                title={t('dailyConditions.inputProfile')}
                value={data.context.user_profile}
                testId="dc-detail-profile"
              />
              <JsonBlock title={t('dailyConditions.inputMemories')} value={data.context.memories} />
              <JsonBlock title={t('dailyConditions.rawResult')} value={data.result} />
            </section>

            <section className="text-xs text-slate-500 space-y-1">
              <h4 className="text-sm font-semibold text-slate-700">{t('dailyConditions.meta')}</h4>
              <div>
                id: <span className="font-mono">{data.id}</span>
              </div>
              <div>
                {t('dailyConditions.batchId')}: <span className="font-mono">{data.batch_id}</span>
              </div>
              <div>
                {t('dailyConditions.colCreated')}: {formatDateTime(data.created_at)}
              </div>
              <div>
                {t('dailyConditions.completedAt')}: {data.completed_at ? formatDateTime(data.completed_at) : '-'} (
                {formatDuration(data.duration_ms)})
              </div>
              <div>guide_followed: {String(data.guide_followed)}</div>
            </section>
          </div>
        )}
      </aside>
    </div>
  );
};

// ── Column docs ─────────────────────────────────────────────

const headerTitle = (key: string): string => {
  const doc = columnDoc(key);
  return doc ? `${key}\n${doc.description}${doc.updated ? `\n갱신: ${doc.updated}` : ''}` : key;
};

const ColumnDocsPanel: React.FC<{ columns: ProfileColumn[]; onClose: () => void }> = ({ columns, onClose }) => {
  const { t } = useTranslation();
  const sections = [
    ...PROFILE_GROUPS.map((group) => ({
      group,
      keys: columns.filter((c) => c.group === group).map((c) => c.key),
    })),
    { group: 'result', keys: RESULT_COLUMNS.map((c) => c.key) },
  ].filter((section) => section.keys.length > 0);

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-label={t('dailyConditions.columnDocs')}>
      <div className="absolute inset-0 bg-slate-900/20" onClick={onClose} />
      <aside
        className="relative w-full max-w-2xl h-full bg-white shadow-xl overflow-y-auto"
        data-testid="dc-column-docs"
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">{t('dailyConditions.columnDocs')}</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-slate-100"
            aria-label={t('dailyConditions.close')}
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-6">
          {sections.map(({ group, keys }) => (
            <section key={group} className="space-y-2">
              <h4 className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${groupStyle[group]}`}>
                {group === 'result' ? t('dailyConditions.aiResult') : group}
              </h4>
              <p className="text-xs text-slate-500">{GROUP_DOCS[group]}</p>
              <dl className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                {keys.map((key) => {
                  const doc = columnDoc(key);
                  return (
                    <div
                      key={key}
                      className="px-3 py-2 grid grid-cols-[13rem_1fr] gap-3 text-sm"
                      data-testid="dc-column-doc"
                    >
                      <dt className="font-mono text-xs text-slate-700 break-all pt-0.5">{key}</dt>
                      <dd>
                        <div className="text-slate-800">{doc?.description ?? t('dailyConditions.noColumnDoc')}</div>
                        {doc?.updated && (
                          <div className="text-xs text-slate-500 mt-0.5">
                            {t('dailyConditions.updatedWhen')}: {doc.updated}
                          </div>
                        )}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </section>
          ))}
        </div>
      </aside>
    </div>
  );
};

// ── Day table ───────────────────────────────────────────────

const groupStyle: Record<string, string> = {
  user: 'bg-sky-50 text-sky-800',
  device: 'bg-violet-50 text-violet-800',
  custom: 'bg-emerald-50 text-emerald-800',
  result: 'bg-amber-50 text-amber-800',
};

// Long free-text result fields wrap instead of truncating
const WIDE_RESULT_KEYS = new Set(['message', 'reason', 'daily_guide.description', 'suspected_injury']);

interface ExpandedCell {
  title: string;
  value: unknown;
}

const JsonModal: React.FC<{ cell: ExpandedCell; onClose: () => void }> = ({ cell, onClose }) => {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-label={cell.title}>
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <div
        className="relative bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[80vh] flex flex-col"
        data-testid="dc-cell-json"
      >
        <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between">
          <span className="font-mono text-sm text-slate-700">{cell.title}</span>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" aria-label={t('dailyConditions.close')}>
            <X size={16} />
          </button>
        </div>
        <pre className="p-4 text-xs text-slate-700 overflow-auto">{JSON.stringify(cell.value, null, 2)}</pre>
      </div>
    </div>
  );
};

const ValueCell: React.FC<{ title: string; value: unknown; onExpand: (cell: ExpandedCell) => void }> = ({
  title,
  value,
  onExpand,
}) => {
  const { text, expandable } = summarize(value);
  const base = 'px-2 py-1.5 text-xs whitespace-nowrap border-r border-slate-100';
  if (value === null) return <td className={`${base} text-slate-300 font-mono`}>null</td>;
  if (typeof value === 'number') return <td className={`${base} text-right tabular-nums text-slate-700`}>{text}</td>;
  if (!expandable) return <td className={`${base} text-slate-700`}>{text}</td>;
  return (
    <td className={base}>
      <button
        onClick={() => onExpand({ title, value })}
        className="max-w-[16rem] truncate text-left text-blue-700 hover:underline"
        title={text}
      >
        {text}
      </button>
    </td>
  );
};

const DayTable: React.FC<{
  rows: DailyConditionTableRow[];
  columns: ProfileColumn[];
  onOpenDetail: (id: string) => void;
}> = ({ rows, columns, onOpenDetail }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState<ExpandedCell | null>(null);

  const groupSpans = PROFILE_GROUPS.map((group) => ({
    group,
    span: columns.filter((c) => c.group === group).length,
  })).filter((g) => g.span > 0);

  const th = 'px-2 py-1.5 text-[11px] font-semibold whitespace-nowrap border-r border-slate-200';
  const stickyId = 'sticky left-0 z-10 bg-white';
  // Fixed widths so the sticky columns' offsets are known
  const stickyName = 'sticky left-[7.5rem] z-10 bg-white w-[8rem] min-w-[8rem] max-w-[8rem] truncate';
  // Group label follows horizontal scroll, parked just right of the sticky columns
  const groupLabel = 'sticky left-[16rem] px-1';

  return (
    <div className="overflow-auto max-h-[calc(100vh-16rem)]" data-testid="dc-day-table">
      <table className="text-left border-separate border-spacing-0">
        <thead className="sticky top-0 z-20">
          <tr>
            <th
              rowSpan={2}
              className={`${th} ${stickyId} z-30 w-[7.5rem] min-w-[7.5rem] bg-slate-100 text-slate-600 border-b`}
            >
              ai_user_id
            </th>
            <th rowSpan={2} className={`${th} ${stickyName} z-30 bg-slate-100 text-slate-600 border-b`}>
              {t('dailyConditions.nickname')}
            </th>
            {groupSpans.map(({ group, span }) => (
              <th key={group} colSpan={span} className={`${th} ${groupStyle[group]} text-left border-b`}>
                <span className={groupLabel}>{group}</span>
              </th>
            ))}
            <th colSpan={RESULT_COLUMNS.length} className={`${th} ${groupStyle.result} text-left border-b`}>
              <span className={groupLabel}>{t('dailyConditions.aiResult')}</span>
            </th>
          </tr>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`${th} ${groupStyle[c.group]} font-mono font-medium border-b`}
                title={headerTitle(c.key)}
              >
                <span className={columnDoc(c.key) ? 'underline decoration-dotted underline-offset-2 cursor-help' : ''}>
                  {c.path.join('.')}
                </span>
              </th>
            ))}
            {RESULT_COLUMNS.map((c) => (
              <th
                key={c.key}
                className={`${th} ${groupStyle.result} font-mono font-medium border-b`}
                title={headerTitle(c.key)}
              >
                <span className="underline decoration-dotted underline-offset-2 cursor-help">{c.key}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="group hover:bg-slate-50" data-testid="dc-day-row">
              <td className={`${stickyId} px-2 py-1.5 border-r border-b border-slate-100 group-hover:bg-slate-50`}>
                <button
                  onClick={() => onOpenDetail(row.id)}
                  className="flex items-center gap-1 font-mono text-[11px] text-slate-500 hover:text-blue-700"
                  title={`${row.user_id} — ${t('dailyConditions.detail')}`}
                >
                  <PanelRightOpen size={12} />
                  {row.user_id.slice(0, 8)}…{row.user_id.slice(-4)}
                </button>
              </td>
              <td
                className={`${stickyName} px-2 py-1.5 text-sm text-slate-800 whitespace-nowrap border-r border-b border-slate-200 group-hover:bg-slate-50`}
                title={row.user_name ?? ''}
              >
                {row.user_name || '-'}
              </td>
              {columns.map((c) => (
                <ValueCell key={c.key} title={c.key} value={profileValue(row, c)} onExpand={setExpanded} />
              ))}
              {RESULT_COLUMNS.map((c) => {
                const value = c.get(row);
                if (c.key === 'risk_flag') {
                  return (
                    <td key={c.key} className="px-2 py-1.5 border-r border-slate-100">
                      <FlagBadge flag={(value as RiskFlag) ?? null} />
                    </td>
                  );
                }
                if (c.key === 'status') {
                  return (
                    <td key={c.key} className="px-2 py-1.5 border-r border-slate-100">
                      <StatusBadge status={value as DailyConditionStatus} />
                    </td>
                  );
                }
                if (WIDE_RESULT_KEYS.has(c.key)) {
                  return (
                    <td
                      key={c.key}
                      className="px-2 py-1.5 text-xs text-slate-700 min-w-[18rem] max-w-[28rem] border-r border-slate-100"
                    >
                      {value == null ? '' : String(value)}
                    </td>
                  );
                }
                return (
                  <td
                    key={c.key}
                    className={`px-2 py-1.5 text-xs whitespace-nowrap border-r border-slate-100 ${c.key === 'error_code' ? 'font-mono text-red-600' : 'text-slate-700'}`}
                  >
                    {value == null ? '' : String(value)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {expanded && <JsonModal cell={expanded} onClose={() => setExpanded(null)} />}
    </div>
  );
};

// ── Page ────────────────────────────────────────────────────

const DailyConditions: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const flag = (searchParams.get('flag') || '') as RiskFlag | '';
  const user = searchParams.get('user') || '';
  // Name terms to exclude, e.g. ?exclude=roopin,test — applies to the table and both CSVs
  const excluded = (searchParams.get('exclude') ?? '')
    .split(',')
    .filter((term): term is ExcludableTerm => (EXCLUDABLE_NAME_TERMS as readonly string[]).includes(term));
  const toggleExcluded = (term: ExcludableTerm, on: boolean) => {
    const next = EXCLUDABLE_NAME_TERMS.filter((t) => (t === term ? on : excluded.includes(t)));
    updateParams({ exclude: next.join(',') || null });
  };
  const selectedId = searchParams.get('id');

  const updateParams = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  const stats = useDailyConditionStats(STATS_DAYS);
  const days = (stats.data?.daily ?? []).map((d) => d.day); // newest first
  const day = searchParams.get('day') || days[0] || '';
  const dayStats = stats.data?.daily.find((d) => d.day === day);
  const dayIndex = days.indexOf(day);

  const table = useDailyConditionTable(day);
  const allRows = React.useMemo(() => table.data?.items ?? [], [table.data]);
  const columns = React.useMemo(() => buildProfileColumns(allRows), [allRows]);

  const [exportProgress, setExportProgress] = React.useState<{ done: number; total: number } | null>(null);
  const [exportError, setExportError] = React.useState(false);
  const [showDocs, setShowDocs] = React.useState(false);

  // Range export: defaults to the whole data period
  const dataFirstDay = stats.data?.first_at ? new Date(stats.data.first_at).toISOString().slice(0, 10) : '';
  const dataLastDay = days[0] ?? '';
  const [rangeOpen, setRangeOpen] = React.useState(false);
  const [rangeStart, setRangeStart] = React.useState('');
  const [rangeEnd, setRangeEnd] = React.useState('');
  const start = rangeStart || dataFirstDay;
  const end = rangeEnd || dataLastDay;
  const rangeValid = !!start && !!end && start <= end;

  // CSVs follow only the account filter — flag/user filters are for browsing
  const forExport = (rows: DailyConditionTableRow[]) =>
    excluded.length ? rows.filter((r) => !isExcludedAccount(r, excluded)) : rows;
  const fileSuffix = excluded.length ? `_no-${excluded.join('-')}` : '';

  const downloadDay = async () => {
    // The table shows at most one page; the file always has the whole day
    const rows = table.data?.truncated ? await dailyConditionsApi.getAllRowsOfDay(day) : allRows;
    downloadCsv(`daily-conditions-${day}${fileSuffix}.csv`, toCsv(forExport(rows)));
  };

  const downloadRange = async () => {
    if (!rangeValid) return;
    const exportDays = utcDaysBetween(`${start}T00:00:00Z`, `${end}T00:00:00Z`);
    const rowsByDay: DailyConditionTableRow[][] = new Array(exportDays.length);
    setExportError(false);
    setExportProgress({ done: 0, total: exportDays.length });
    try {
      let next = 0;
      let done = 0;
      const worker = async () => {
        while (next < exportDays.length) {
          const index = next++;
          rowsByDay[index] = await dailyConditionsApi.getAllRowsOfDay(exportDays[index]);
          setExportProgress({ done: ++done, total: exportDays.length });
        }
      };
      await Promise.all(Array.from({ length: EXPORT_CONCURRENCY }, worker));

      const dayById = new Map<string, string>();
      rowsByDay.forEach((rows, i) => rows.forEach((row) => dayById.set(row.id, exportDays[i])));
      const rows = forExport(rowsByDay.flat());
      downloadCsv(
        `daily-conditions-${start}_${end}${fileSuffix}.csv`,
        toCsv(rows, (row) => dayById.get(row.id) ?? ''),
      );
      setRangeOpen(false);
    } catch {
      setExportError(true);
    } finally {
      setExportProgress(null);
    }
  };

  const needle = user.trim().toLowerCase();
  const rows = allRows.filter(
    (r) =>
      !isExcludedAccount(r, excluded) &&
      (!flag || r.result?.risk_flag === flag) &&
      (!needle || r.user_id.includes(needle) || (r.user_name ?? '').toLowerCase().includes(needle)),
  );

  const selectClass = 'border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white text-slate-700';
  const navButton =
    'p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold text-slate-800">{t('dailyConditions.title')}</h2>
          <p className="text-sm text-slate-500 mt-1">{t('dailyConditions.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 whitespace-nowrap">
          <button
            onClick={() => setShowDocs(true)}
            className="flex items-center gap-2 text-sm bg-white border border-slate-200 px-3 py-1.5 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
            data-testid="dc-open-column-docs"
          >
            <BookOpen size={14} />
            {t('dailyConditions.columnDocs')}
          </button>
          <button
            onClick={() => {
              stats.refetch();
              table.refetch();
            }}
            className="flex items-center gap-2 text-sm bg-white border border-slate-200 px-3 py-1.5 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <RefreshCw size={14} className={stats.isFetching || table.isFetching ? 'animate-spin' : ''} />
            {t('dailyConditions.refresh')}
          </button>
          <button
            onClick={downloadDay}
            disabled={!allRows.length || table.isPlaceholderData}
            className="flex items-center gap-2 text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-40 transition-colors"
            data-testid="dc-download-csv"
          >
            <Download size={14} />
            {t('dailyConditions.downloadCsv')}
          </button>
          <div className="relative">
            <button
              onClick={() => setRangeOpen((open) => !open)}
              disabled={!stats.data?.first_at}
              className="flex items-center gap-2 text-sm bg-white border border-blue-600 text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-50 disabled:opacity-40 transition-colors"
              data-testid="dc-open-range-csv"
              aria-expanded={rangeOpen}
            >
              {exportProgress ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {exportProgress
                ? t('dailyConditions.exportProgress', exportProgress)
                : t('dailyConditions.downloadRangeCsv')}
            </button>
            {rangeOpen && (
              <div
                className="absolute right-0 top-full mt-2 z-30 w-80 whitespace-normal bg-white border border-slate-200 rounded-xl shadow-lg p-4 space-y-3"
                data-testid="dc-range-csv"
              >
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex flex-col gap-1 text-xs text-slate-500">
                    <span>{t('dailyConditions.rangeStart')}</span>
                    <input
                      type="date"
                      value={start}
                      min={dataFirstDay}
                      max={end || dataLastDay}
                      onChange={(e) => setRangeStart(e.target.value)}
                      className={`${selectClass} w-full`}
                      data-testid="dc-range-start"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-slate-500">
                    <span>{t('dailyConditions.rangeEnd')}</span>
                    <input
                      type="date"
                      value={end}
                      min={start || dataFirstDay}
                      max={dataLastDay}
                      onChange={(e) => setRangeEnd(e.target.value)}
                      className={`${selectClass} w-full`}
                      data-testid="dc-range-end"
                    />
                  </label>
                </div>
                <p className="text-xs text-slate-500">
                  {t('dailyConditions.rangeNote')}
                  {excluded.length > 0 && ` ${t('dailyConditions.rangeNoteExcluded', { terms: excluded.join(', ') })}`}
                </p>
                <button
                  onClick={downloadRange}
                  disabled={!rangeValid || exportProgress !== null}
                  className="w-full flex items-center justify-center gap-2 text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-40 transition-colors"
                  data-testid="dc-download-range-csv"
                >
                  {exportProgress ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  {exportProgress
                    ? t('dailyConditions.exportProgress', exportProgress)
                    : t('dailyConditions.downloadCsvAction')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {(stats.isError || table.isError) && <p className="text-sm text-red-600">{t('dailyConditions.loadError')}</p>}
      {exportError && <p className="text-sm text-red-600">{t('dailyConditions.exportError')}</p>}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-3 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center gap-2">
          <button
            className={navButton}
            disabled={dayIndex === -1 || dayIndex >= days.length - 1}
            onClick={() => updateParams({ day: days[dayIndex + 1], id: null })}
            aria-label={t('dailyConditions.prevDay')}
          >
            <ChevronLeft size={16} />
          </button>
          <input
            type="date"
            value={day}
            onChange={(e) => updateParams({ day: e.target.value, id: null })}
            className={selectClass}
            aria-label={t('dailyConditions.filterDay')}
            data-testid="dc-filter-day"
          />
          <button
            className={navButton}
            disabled={dayIndex <= 0}
            onClick={() => updateParams({ day: days[dayIndex - 1], id: null })}
            aria-label={t('dailyConditions.nextDay')}
          >
            <ChevronRight size={16} />
          </button>

          {dayStats && (
            <span className="text-sm text-slate-600 ml-1" data-testid="dc-day-summary">
              {t('dailyConditions.daySummary', { users: dayStats.users, completed: dayStats.completed })}{' '}
              <span className="text-green-700">G {dayStats.green}</span> ·{' '}
              <span className="text-amber-700">Y {dayStats.yellow}</span> ·{' '}
              <span className="text-red-700">R {dayStats.red}</span>
              {dayStats.failed > 0 && (
                <span className="text-red-600 font-semibold">
                  {' '}
                  · {t('dailyConditions.failed')} {dayStats.failed}
                </span>
              )}
            </span>
          )}

          <div className="ml-auto flex items-center gap-2">
            <select
              value={flag}
              onChange={(e) => updateParams({ flag: e.target.value })}
              className={selectClass}
              aria-label={t('dailyConditions.filterFlag')}
              data-testid="dc-filter-flag"
            >
              <option value="">
                {t('dailyConditions.filterFlag')}: {t('dailyConditions.all')}
              </option>
              {FLAGS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
            <span className="text-sm text-slate-500 whitespace-nowrap">{t('dailyConditions.excludeAccounts')}</span>
            {EXCLUDABLE_NAME_TERMS.map((term) => (
              <label key={term} className="flex items-center gap-1.5 text-sm text-slate-600 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={excluded.includes(term)}
                  onChange={(e) => toggleExcluded(term, e.target.checked)}
                  data-testid={`dc-exclude-${term}`}
                />
                {term}
              </label>
            ))}
            <input
              type="search"
              value={user}
              onChange={(e) => updateParams({ user: e.target.value })}
              placeholder={t('dailyConditions.filterUser')}
              className={`${selectClass} w-56`}
              data-testid="dc-filter-user"
            />
            {table.data && (
              <span className="text-xs text-slate-500 tabular-nums" data-testid="dc-row-count">
                {rows.length === allRows.length ? allRows.length : `${rows.length} / ${allRows.length}`}
              </span>
            )}
          </div>
        </div>

        {table.data?.truncated && (
          <p className="px-4 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-200">
            {t('dailyConditions.truncated', { count: allRows.length })}
          </p>
        )}

        {table.isLoading || (stats.isLoading && !day) ? (
          <div className="p-10 flex justify-center">
            <Loader2 className="animate-spin text-slate-400" />
          </div>
        ) : rows.length > 0 ? (
          <div className={table.isPlaceholderData ? 'opacity-60' : ''}>
            <DayTable rows={rows} columns={columns} onOpenDetail={(id) => updateParams({ id })} />
          </div>
        ) : (
          !table.isError && (
            <div className="p-8 text-center text-slate-500">
              <HeartPulse size={32} className="mx-auto mb-2 text-slate-300" />
              <p>{t('dailyConditions.empty')}</p>
            </div>
          )
        )}
      </div>

      {showDocs && <ColumnDocsPanel columns={columns} onClose={() => setShowDocs(false)} />}
      {selectedId && <DetailPanel id={selectedId} onClose={() => updateParams({ id: null })} />}
    </div>
  );
};

export default DailyConditions;
